/** @jsx jsx */
import { useState } from 'react'
import { Link } from 'gatsby'
import { Box, Button, Text, jsx } from 'theme-ui'
import ChevronIcon from '~/assets/images/icons/chevron.inline.svg'
import GiftIcon from '~/assets/images/icons/gift.inline.svg'
import VisuallyHidden from '~/components/Generic/VisuallyHidden'
import NavMenuSubmenu from '~/components/Nav/Menu/Submenu'
import { useAria } from '~/hooks/components/use-aria'

const NavMenuLink = ({ page, closeParentMenu }) => {
  const { updateAriaLive } = useAria()
  const [isOpen, setIsOpen] = useState(false)

  const giftIconEnabled = process.env.GATSBY_NAV_GIFT_ICON_ENABLED

  const primaryItemSx = {
    variant: 'text.navLink',
    borderBottom: ['1px solid', null, '0px'],
    borderBottomColor: 'white',
    color: ['white', null, 'black'],
    cursor: 'pointer',
    display: 'block',
    lineHeight: [null, null, '64px'],
    p: ['30px 0 28px', null, '0 12px', '0 16px'],
    position: 'relative',
    textAlign: 'center',
    textDecoration: 'none',
    textTransform: 'uppercase',
    '&:hover': {
      color: [null, null, 'brandTertiary'],
    },
    // Temporary fix with longer nav titles in place.
    '@media only screen and (min-width: 832px) and (max-width: 900px)': {
      fontSize: '14px',
      pl: '10px',
    },
  }

  const handleClick = (e) => {
    if (e.target.tagName === 'A') {
      closeParentMenu()
    } else {
      setIsOpen(!isOpen)
      updateAriaLive(`Sub-navigation ${isOpen ? 'closed' : 'opened'}.`)
    }
  }

  return (
    <Box
      as="li"
      sx={{
        display: 'block',
        position: 'relative',
        '&:hover svg': {
          color: [null, null, 'brandTertiary'],
        },
      }}
    >
      {page.slug && (
        <Link
          key={page.id}
          to={`/${page.path || page.slug}/`}
          sx={primaryItemSx}
        >
          {giftIconEnabled && page?.headerNavTitle?.includes('Gifts') && (
            <GiftIcon
              sx={{
                color: 'brandTertiary',
                left: ['-8px', '-6px'],
                pointerEvents: 'none',
                position: 'relative',
                top: '2px',
              }}
            />
          )}
          {page.headerNavTitle || page.name}
        </Link>
      )}
      {!page.slug && page.pages && (
        <Box>
          <Button
            onClick={(e) => handleClick(e)}
            variant="reset"
            sx={{ width: ['100%', null, 'auto'] }}
          >
            <Text
              as="p"
              aria-hidden="true"
              key={page.id}
              sx={{
                ...primaryItemSx,
                pr: [null, null, 3, 4],
                position: 'relative',
                '*:hover > &': {
                  color: [null, null, 'brandTertiary'],
                },
              }}
            >
              {giftIconEnabled && page?.title?.includes('Gifts') && (
                <GiftIcon
                  sx={{
                    color: 'brandTertiary',
                    left: ['-8px', '-6px'],
                    pointerEvents: 'none',
                    position: 'relative',
                    top: '2px',
                  }}
                />
              )}
              {page.title}
              <ChevronIcon
                sx={{
                  color: ['white', null, 'black'],
                  display: 'block',
                  height: '8px',
                  pointerEvents: 'none',
                  position: 'absolute',
                  right: ['5px', null, null, '15px'],
                  top: ['38px', null, '30px'],
                  transform: [isOpen ? 'rotate(180deg)' : 'none', null, 'none'],
                  width: '12px',
                }}
              />
            </Text>
            <VisuallyHidden>
              <Text as="p">{`${page.title} sub-navigation, contains ${page.pages.length} items, click to open.`}</Text>
            </VisuallyHidden>
          </Button>
          <NavMenuSubmenu isOpen={isOpen} page={page} />
        </Box>
      )}
    </Box>
  )
}

export default NavMenuLink
